import { getMyReports } from "@/Api/ReportApi";
import { usePopups } from "@/Hooks/usePopups";
import { GetMyReportsInputSchema } from "@/Schemas/Reports/GetMyReportsInput";
import { GetMyReportsOutput, GetMyReportsOutputSchema } from "@/Schemas/Reports/GetMyReportsOutput";
import { Box, Icon, IconButton, LinearProgress, Skeleton, Typography, useTheme, Tooltip as MuiTooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { IoInformationCircle, IoPlayCircle } from "react-icons/io5";
import { FixedSizeList, ListChildComponentProps, ListOnScrollProps } from "react-window";
import { v1 } from "uuid";
import { SnackbarProvider, SnackbarType, TextOverflow, Tooltip, UseTaskReturn, capitalizeSentences, getXCenterStyles, getYCenterStyles, useTask } from "vig-ui-library";
import AutoSizer from "react-virtualized-auto-sizer";
import { MyReportsDto } from "@/Schemas/Reports/MyReportsDto";
import { DataNotFound } from "../DataNotFound";
import { IoAlarm } from "react-icons/io5";
import { GetMyReportsExecutionHistoryOutput } from "@/Schemas/Reports/GetMyReportsExecutionHistoryOutput";

const LIST_ITEM_SIZE = 50;

export type MyAssembliesListProps = {
    bigView: boolean;
    refreshExecutionHistoryTask: UseTaskReturn<[], GetMyReportsExecutionHistoryOutput>;
}

export const MyAssembliesList = ({ bigView, refreshExecutionHistoryTask }: MyAssembliesListProps) => {
    const theme = useTheme();

    const { openSnackbar } = SnackbarProvider.useSnackbar();

    const virtualizedListMounted = useRef(false);

    const virtualizedListRef = useRef<FixedSizeList | null>(null);

    const input = useRef(GetMyReportsInputSchema.parse({}));

    const [data, setData] = useState<GetMyReportsOutput>(GetMyReportsOutputSchema.parse({ items: [] }));

    const { perform, isRunning, isInitialCall } = useTask(async () => {
        try {
            const response = await getMyReports(input.current);
            const newData = { ...data, ...response, items: [...data.items, ...response.items] };
            setData(newData);
        } catch (err) {
            openSnackbar({
                text: "Nepodarilo sa načítať zostavy, skúste to znova.",
                type: SnackbarType.ERROR,
            });
        }
    });

    const onHitBottom = () => {
        if (isRunning) return;

        const hasMoreData = data.items.length < data.count;
        if (!hasMoreData) return;

        input.current = { ...input.current, page: input.current.page + 1 }

        perform();
    }

    const onScroll = (scroll: ListOnScrollProps) => {
        if (!virtualizedListMounted.current) {
            virtualizedListMounted.current = true;
            return;
        }

        const totalHeight = data.items.length * LIST_ITEM_SIZE;

        if (totalHeight === 0) return;

        const clientHeight = virtualizedListRef.current?.props.height as number;

        if (scroll.scrollOffset >= totalHeight - clientHeight) onHitBottom();
    }

    useEffect(() => {
        perform()
    }, []);

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            height: "100%",
            width: bigView ? 420 : "100%",
            flexShrink: 0,
            borderRight: `1px solid ${theme.palette.divider}`,
        }}>
            <LinearProgress sx={{ visibility: !isInitialCall && isRunning ? "visible" : "hidden", width: "100%" }} />

            <Box sx={{ padding: 2, gap: 1 }}>
                <Typography variant="h6">Moje zostavy</Typography>

                <Typography variant="body2">
                    V tejto sekcií máte zostavy ktoré môžete spustiť,
                    z ktorých sa vám vygeneruje export
                </Typography>
            </Box>

            <Box sx={{ flex: '1 1 auto', flexGrow: 1, display: "flex", width: "100%", overflowX: "hidden" }}>
                {isRunning && isInitialCall && <MyAssembliesListSkeleton />}

                {(!isRunning || !isInitialCall) && !!data.items.length && (
                    <AutoSizer>
                        {({ height, width }) => {
                            return <FixedSizeList
                                ref={virtualizedListRef}
                                height={height}
                                width={width}
                                itemSize={LIST_ITEM_SIZE}
                                itemCount={data.items.length}
                                onScroll={onScroll}
                            >
                                {(props) => RenderMyAssembly({
                                    ...props,
                                    assembly: data.items[props.index],
                                    refreshExecutionHistoryTask,
                                })}
                            </FixedSizeList>
                        }}
                    </AutoSizer>
                )}

                {!isRunning && !data.items.length && <DataNotFound
                    title="Žiadne zostavy"
                    description="Nemáte žiadne zostavy ktoré by ste mohli spustiť"
                />}
            </Box>
        </Box>
    );
}

type RenderMyAssemblyProps = ListChildComponentProps & {
    assembly: MyReportsDto;
    refreshExecutionHistoryTask: UseTaskReturn<[], GetMyReportsExecutionHistoryOutput>;
}

const RenderMyAssembly = (props: RenderMyAssemblyProps) => {
    const { style, assembly, refreshExecutionHistoryTask } = props;

    const { openRunAssembly } = usePopups();

    const theme = useTheme();

    return (
        <div style={style} onClick={(e) => {
            e.stopPropagation();
            openRunAssembly(assembly.id, assembly.name, refreshExecutionHistoryTask);
        }}>
            <Box sx={{
                paddingX: 2,
                height: 50,
                overflow: "hidden",
                cursor: "pointer",
                transition: "background-color 0.3s ease",
                "&:hover": {
                    backgroundColor: theme.palette.grey[100],
                }
            }}>
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: 1,
                    gap: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    height: "100%",
                }}>
                    <Box sx={{ flexShrink: 0, fontSize: 28 }}>
                        <Icon sx={{ color: theme.palette.common.black }} color="secondary">{assembly.icon}</Icon>
                    </Box>

                    <MuiTooltip title={capitalizeSentences(assembly.name)}>
                        <Box sx={{ flexGrow: 1, display: "flex", width: "100%", overflow: "hidden" }}>
                            <TextOverflow maxWidth="100%" text={capitalizeSentences(assembly.name)} />
                        </Box>
                    </MuiTooltip>

                    <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                        {assembly.isPeriodicReport && (
                            <Tooltip mobileHeader="" bodyBuilder={(mobileVersion) => {
                                const textColor = mobileVersion ? theme.palette.text.primary : theme.palette.common.white;
                                return <Typography variant="body2" sx={{ color: textColor }}>
                                    Periodická zostava
                                </Typography>
                            }}>
                                <IconButton color="info" size="small">
                                    <IoAlarm size={24} />
                                </IconButton>
                            </Tooltip>
                        )}

                        <Tooltip mobileHeader={assembly.name} bodyBuilder={(mobileVersion: boolean) => {
                            const textColor = mobileVersion ? theme.palette.text.primary : theme.palette.common.white;
                            return <Typography variant="body2" sx={{ color: textColor, whiteSpace: 'pre-line' }}>
                                {assembly.description}
                            </Typography>
                        }}>
                            <IconButton color="secondary" size="small">
                                <IoInformationCircle size={24} />
                            </IconButton>
                        </Tooltip>

                        <IconButton color="success" size="small" onClick={(e) => {
                            e.stopPropagation();
                            openRunAssembly(assembly.id, assembly.name, refreshExecutionHistoryTask);
                        }}>
                            <IoPlayCircle size={28} />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
        </div>
    )

}

const MyAssembliesListSkeleton = () => {
    return <Box sx={{ ...getXCenterStyles("column"), padding: 2, width: "100%" }}>
        {Array.from({ length: 18 }).map((_i) => <AssemblyListItemSkeleton key={v1()} />)}
    </Box>
}

const AssemblyListItemSkeleton = () => {
    return <Box sx={{ ...getYCenterStyles(), width: "100%", gap: 2, height: LIST_ITEM_SIZE, flexShrink: 0 }}>
        <Skeleton variant="circular" width={28} height={28} sx={{ flexShrink: 0 }} />

        <Box sx={{ ...getYCenterStyles("column"), width: "100%", flexGrow: 1 }}>
            <Skeleton variant="text" width="100%" height={16} />
            <Skeleton variant="text" width="80%" height={16} />
        </Box>

        <Skeleton variant="rounded" width={24} height={24} sx={{ flexShrink: 0 }} />

        <Skeleton variant="rounded" width={24} height={24} sx={{ flexShrink: 0 }} />
    </Box>
}